import * as React from 'react';
import { cn } from '../utils/cn';

// ─── TabBar.Item ────────────────────────────────────────────────────────

export interface TabBarItemProps {
  /** Unique key */
  key?: string;
  /** Tab title text */
  title?: React.ReactNode;
  /** Default icon */
  icon?: React.ReactNode;
  /** Selected icon */
  selectedIcon?: React.ReactNode;
  /** Whether this tab is selected */
  selected?: boolean;
  /** Press handler */
  onPress?: () => void;
  /** Badge content */
  badge?: React.ReactNode;
}

export function TabBarItem({
  title,
  icon,
  selectedIcon,
  selected = false,
  onPress,
  badge,
}: TabBarItemProps) {
  return (
    <button
      onClick={onPress}
      className={cn(
        'tab-bar-tab',
        'cursor-pointer',
        selected && 'tab-bar-tab-selected'
      )}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: '6px 0',
        position: 'relative',
        height: 'auto',
        border: 'none',
        background: 'transparent',
        letterSpacing: 'normal',
      }}
      role="tab"
      aria-selected={selected}
    >
      {/* Icon */}
      <div className="relative tab-bar-tab-icon">
        {selected && selectedIcon ? selectedIcon : icon}
        {badge && (
          <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center bg-destructive text-white text-[10px] font-bold rounded-full">
            {badge}
          </span>
        )}
      </div>
      {title && (
        <span className={cn(
          'tab-bar-tab-title',
          'text-[11px] mt-0.5 normal-case !tracking-normal',
          selected ? 'font-medium' : 'font-normal'
        )}>
          {title}
        </span>
      )}
    </button>
  );
}

// ─── TabBar ─────────────────────────────────────────────────────────────

export interface TabBarProps {
  /** Unselected tab text/icon color (antd-mobile compat — use CSS instead) */
  unselectedTintColor?: string;
  /** Selected tab text/icon color (antd-mobile compat — use CSS instead) */
  tintColor?: string;
  /** Background color (antd-mobile compat — use CSS instead) */
  barTintColor?: string;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** TabBar.Item children */
  children?: React.ReactNode;
}

function TabBarBase({
  unselectedTintColor,
  tintColor,
  barTintColor,
  className,
  style,
  children,
}: TabBarProps) {
  return (
    <nav
      className={cn(
        'tab-bar',
        'flex flex-row flex-nowrap items-stretch',
        className
      )}
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        width: '100%',
        backgroundColor: barTintColor || 'transparent',
        ...style,
      }}
      role="tablist"
    >
      {children}
    </nav>
  );
}

// Compound component for antd-mobile compat: TabBar.Item
export const TabBar = Object.assign(TabBarBase, { Item: TabBarItem });
